import React, {CSSProperties, useState} from 'react';
import {ThumbnailProps} from './MediaGalleryThumbnailFactory';
import ThumbnailImage from './ThumbnailImage';
import ThumbnailOverlays from './ThumbnailOverlays';
import ThumbnailSelector from './ThumbnailSelector';
import {SelectionMode} from '../MediaGallery';
import {useInView} from 'react-intersection-observer';
import classNames from 'classnames';
import {useTranslation} from 'react-i18next';

export type ThumbnailBaseProps = {
  imageClasses?: string;
  imageCSS?: CSSProperties;
} & ThumbnailProps;

const ThumbnailBase = ({
  image,
  imageClasses,
  imageCSS,
  openDetailsDialog,
  used,
  onSelectPicture,
  selectableImages,
  onDragStart,
  onDragEnd,
  onTouchStart,
  onTouchEnd,
  onDeleteAsset,
  selected,
  localeString,
  currencyCode,
  expired,
  showPricing,
  showLabel,
  selectionAppearance,
  disabled,
  overlayDisplayMode,
  showDeleteButton,
  selectionMode = SelectionMode.Multi,
  maxSelectionReached = false,
  thumbnailLayout,
  onKeyDown,
}: ThumbnailBaseProps) => {
  const {t} = useTranslation(['*']);
  const [ref, inView] = useInView({
    threshold: 0.1,
  });

  const [loaded, setLoaded] = useState(false);

  const toggleChecked = (checked: boolean) => {
    if (onSelectPicture) {
      onSelectPicture(checked, image);
    }
  };

  const onKeyPress = (event: React.KeyboardEvent) => {
    if (onKeyDown) {
      onKeyDown(event, image);
    }
  };

  const thumbnailContainerClasses = classNames('w-full', 'aspect-[var(--asset-ratio,1)]');

  return (
    <div ref={ref} className={thumbnailContainerClasses}>
      {inView && (
        <>
          <ThumbnailSelector
            appearance={selectionAppearance}
            label={t('*:str_LabelSelectImage', {defaultValue: 'Select image'}) + image.id}
            selectable={loaded && selectableImages && !expired && !disabled}
            selected={selected}
            onToggle={toggleChecked}
            selectionMode={selectionMode}
            onKeyDown={onKeyPress}
          >
            <ThumbnailImage
              selectable={selectableImages}
              classNames={imageClasses}
              imageCSS={imageCSS}
              disabled={disabled}
              expired={expired}
              image={image}
              onDragEnd={onDragEnd || undefined}
              onDragStart={onDragStart || undefined}
              onTouchEnd={onTouchEnd || undefined}
              onTouchStart={onTouchStart || undefined}
              setLoaded={setLoaded || undefined}
              thumbnailLayout={thumbnailLayout}
            />
          </ThumbnailSelector>

          <ThumbnailOverlays
            currencyCode={currencyCode}
            disabled={disabled}
            expired={expired}
            image={image}
            localeString={localeString}
            openDetailsDialog={openDetailsDialog || undefined}
            showPricing={showPricing}
            used={used}
            onDeleteAsset={onDeleteAsset}
            displayMode={overlayDisplayMode}
            showDeleteButton={showDeleteButton}
            showLabel={showLabel}
            maxSelectionReached={maxSelectionReached}
          />
        </>
      )}
    </div>
  );
};

export default ThumbnailBase;
